import { useMemo } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useReso } from '../contexts/ResoContext';
import { ExternalAuthCheck } from './externalAuthPipeline';
import { createResoCheck } from './checks/resoCheck';
import { createPromoteCheck } from './checks/promoteCheck';

/**
 * Builds the external auth check list from tenant config.
 *
 * Shared by AuthCoordinator (full pipeline with loading states) and
 * AuthSuccess (sequential run after NGW login redirect).
 *
 * To add a new external service auth, add another entry to the array.
 */
export function useExternalAuthChecks(): ExternalAuthCheck[] {
  const {
    supportsClientResoWebApi,
    supportsPromoteAuth,
    getConfigValue,
  } = useTenant();

  const { setAuthenticated: setResoAuthenticated } = useReso();

  return useMemo(
    () => [
      {
        name: 'reso',
        enabled: supportsClientResoWebApi(),
        checkSession: async () => {
          const check = createResoCheck(
            getConfigValue('RESO_BASE_URL'),
            getConfigValue<boolean>('RESO_DEBUG_MODE', false),
          );
          const result = await check();
          if (result) {
            setResoAuthenticated(true);
          }
          return result;
        },
        sessionStorageKey: 'reso_auth_in_progress',
        loadingMessage: 'Establishing secure connection...',
      },
      {
        name: 'promote',
        enabled: supportsPromoteAuth(),
        checkSession: createPromoteCheck(
          getConfigValue('PROMOTE_BASE_URL'),
        ),
        sessionStorageKey: 'promote_auth_in_progress',
        loadingMessage: 'Connecting to Promote...',
      },
    ],
    [
      supportsClientResoWebApi,
      supportsPromoteAuth,
      setResoAuthenticated,
      getConfigValue,
    ],
  );
}
